import React from 'react';
import { Phone } from 'lucide-react';
import {
  FRONT_DESK_PHONE_DISPLAY,
  FRONT_DESK_HOURS,
  FRONT_DESK_LABEL,
  FRONT_DESK_TEL_HREF,
} from '../lib/siteContact';

/**
 * CallFrontDeskLine
 *
 * A small, intentionally-low-key line that sits directly beneath any
 * Discovery Call CTA across the site. The goal: give prospects an
 * alternative high-touch path (a phone call to the front desk) without
 * competing visually with the primary booking action.
 *
 * Variants:
 *  - `tone="dark"` (default)  — white-text variant for use on dark sections
 *                              like the hero, FinalCTABanner, etc.
 *  - `tone="light"`           — ink-text variant for cream/white sections.
 *  - `align`                  — 'center' (default) | 'left'
 *  - `size`                   — 'sm' (default) | 'md' (slightly larger)
 *  - `testId`                 — unique data-testid for tracking which surface
 *                              the click came from.
 */
export const CallFrontDeskLine = ({
  tone = 'dark',
  align = 'center',
  size = 'sm',
  testId = 'call-front-desk-line',
}) => {
  const textColor = tone === 'dark' ? 'text-white/55' : 'text-foreground/60';
  const linkColor =
    tone === 'dark'
      ? 'text-white hover:text-[#4FBCC7]'
      : 'text-[#004C54] hover:text-[#003a40]';
  const iconColor = tone === 'dark' ? 'text-[#4FBCC7]' : 'text-[#004C54]';
  const alignClass = align === 'left' ? 'justify-start text-left' : 'justify-center text-center';
  const sizeClass = size === 'md' ? 'text-[13.5px]' : 'text-[12px] sm:text-[12.5px]';

  return (
    <p
      className={`inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5 ${alignClass} ${sizeClass} ${textColor} leading-snug`}
      data-testid={testId}
    >
      <Phone className={`h-3.5 w-3.5 ${iconColor} flex-shrink-0`} aria-hidden="true" />
      <span>Prefer to talk first?</span>
      <a
        href={FRONT_DESK_TEL_HREF}
        className={`font-semibold underline underline-offset-4 decoration-2 decoration-current/30 hover:decoration-current ${linkColor} transition-colors`}
        data-testid={`${testId}-link`}
      >
        Call the {FRONT_DESK_LABEL}: {FRONT_DESK_PHONE_DISPLAY}
      </a>
      <span className="opacity-70">({FRONT_DESK_HOURS})</span>
    </p>
  );
};

export default CallFrontDeskLine;
